import { useState } from "react";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { Button, InlineStack, Banner, Text } from "@shopify/polaris";
import { useTranslation } from "react-i18next";
import { setIsModalxOpen, setDestroy } from "components/modalx/modalx.slice";
import { createFinanceDraft } from "services/customers.service";

interface CreateFinanceYearConfirmProps {
  companyId: string;
  customerId: string;
  year: number;
}

export default function CreateFinanceYearConfirm({
  companyId,
  customerId,
  year,
}: CreateFinanceYearConfirmProps) {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [errMsg, setErrMsg] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const handleConfirm = async () => {
    if (!companyId || !customerId) return;
    try {
      setIsLoading(true);
      setErrMsg(null);
      const { financeRecord } = await createFinanceDraft(companyId, customerId, year);
      if (!financeRecord?.id) {
        setErrMsg("error");
        setIsLoading(false);
        return;
      }
      dispatch(setIsModalxOpen(false));
      dispatch(setDestroy());
      navigate(`/app/${companyId}/customers/${customerId}/finance/${year}/${financeRecord.id}`);
    } catch {
      setErrMsg("error");
      setIsLoading(false);
    }
  };

  const handleCancel = () => {
    dispatch(setIsModalxOpen(false));
    dispatch(setDestroy());
  };

  return (
    <>
      {errMsg && (
        <Banner tone="critical" onDismiss={() => setErrMsg(null)}>
          {t(errMsg)}
        </Banner>
      )}
      <Text as="p">
        {t("create_new_year_approval_confirm", { year })}
      </Text>
      <InlineStack align="space-between" blockAlign="center" gap="400">
        <Button variant="primary" onClick={handleConfirm} loading={isLoading}>
          {t("Yes")}
        </Button>
        <Button onClick={handleCancel} disabled={isLoading}>
          {t("No")}
        </Button>
      </InlineStack>
    </>
  );
}
